/**
 * usePermissions Hook
 * Provides role-based access control for CamPassed routes
 */

import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';

// Route permissions by role
const ROLE_PERMISSIONS = {
  // Organization roles
  owner: [
    'command-center', 'events', 'guests', 'scanner', 'members', 'settings', 'billing'
  ],
  admin: [
    'command-center', 'events', 'guests', 'scanner', 'members', 'settings', 'billing'
  ],
  event_manager: [
    'command-center', 'events', 'guests', 'scanner'
  ],
  door_staff: [
    'scanner'
  ],
  member: [
    // Members can only see student routes (discover, my-pass)
  ],
  
  // Platform roles
  platform_owner: [
    'platform-dashboard', 'campuses', 'organizations', 'platform-events', 
    'revenue', 'users', 'support', 'platform-settings', 'sponsors', 'ads', 'placements', 'help'
  ],
  platform_admin: [
    'platform-dashboard', 'campuses', 'organizations', 'platform-events', 
    'revenue', 'users', 'support', 'platform-settings', 'sponsors', 'ads', 'placements', 'help'
  ],
  platform_support: [
    'platform-dashboard', 'organizations', 'platform-events', 'users', 'support', 'help'
  ],
};

// Student routes (available to all authenticated users)
const STUDENT_ROUTES = ['discover', 'my-pass', 'help'];

// Map route paths to permission keys
const PATH_TO_PERMISSION = {
  // Org routes
  '/app/command-center': 'command-center',
  '/app/events': 'events',
  '/app/guests': 'guests',
  '/app/scanner': 'scanner',
  '/app/members': 'members',
  '/app/settings': 'settings',
  '/app/billing': 'billing',
  '/app/monetization': 'billing',  // Monetization uses billing permission
  
  // Platform routes
  '/platform': 'platform-dashboard',
  '/platform/campuses': 'campuses',
  '/platform/organizations': 'organizations',
  '/platform/events': 'platform-events',
  '/platform/revenue': 'revenue',
  '/platform/users': 'users',
  '/platform/support': 'support',
  '/platform/settings': 'platform-settings',
  
  // Student routes
  '/discover': 'discover',
  '/my-pass': 'my-pass',
};

export const usePermissions = () => {
  const { user, profile, organization, orgRole } = useAuth();

  const permissions = useMemo(() => {
    // Helper booleans
    const isAuthenticated = !!user;
    const isPlatformUser = !!profile?.platform_role;
    const isOrgUser = !!organization && !!orgRole;
    const isStudent = isAuthenticated && !isPlatformUser && !isOrgUser;
    
    // Get current role
    const role = isPlatformUser 
      ? profile.platform_role 
      : isOrgUser 
        ? orgRole 
        : 'student';
    
    // Get allowed routes for role
    const allowedRoutes = ROLE_PERMISSIONS[role] || [];
    
    // Platform admins can also access org routes if they want
    const isPlatformAdmin = ['platform_owner', 'platform_admin'].includes(profile?.platform_role);
    
    /**
     * Check if user can view a specific route
     * @param {string} routeNameOrPath - Route name (e.g., 'events') or full path (e.g., '/app/events')
     * @returns {boolean}
     */
    const canView = (routeNameOrPath) => {
      if (!isAuthenticated) return false;
      
      // Normalize to permission key
      const permissionKey = PATH_TO_PERMISSION[routeNameOrPath] || routeNameOrPath;
      
      // Student routes are available to all authenticated users
      if (STUDENT_ROUTES.includes(permissionKey)) {
        return true;
      }
      
      // Platform admins can access everything
      if (isPlatformAdmin) {
        return true;
      }
      
      // Check role-based permissions
      return allowedRoutes.includes(permissionKey);
    };
    
    /**
     * Check if user can access any org routes
     */
    const canAccessOrgRoutes = () => {
      if (!isAuthenticated) return false;
      if (isPlatformAdmin) return true;
      return isOrgUser && allowedRoutes.length > 0;
    };
    
    /**
     * Check if user can access platform routes
     */
    const canAccessPlatformRoutes = () => {
      if (!isAuthenticated) return false;
      return isPlatformUser;
    };
    
    /**
     * Get the default redirect path for the user
     */
    const getDefaultPath = () => {
      if (isPlatformUser) {
        return '/platform';
      }
      if (isOrgUser) {
        // Door staff goes straight to scanner
        if (orgRole === 'door_staff') {
          return '/app/scanner';
        }
        return '/app/command-center';
      }
      return '/discover';
    };
    
    /**
     * Filter nav items based on permissions
     * @param {Array} items - Array of nav items with 'path' property
     * @returns {Array} - Filtered items user can access
     */
    const filterNavItems = (items) => {
      return items.filter(item => canView(item.path));
    };
    
    /**
     * Check if user has a specific org role
     */
    const hasOrgRole = (roles) => {
      if (!isOrgUser) return false;
      if (Array.isArray(roles)) {
        return roles.includes(orgRole);
      }
      return orgRole === roles;
    };
    
    /**
     * Check if user is door staff (for special minimal UI)
     */
    const isDoorStaff = orgRole === 'door_staff' && !isPlatformAdmin;
    
    return {
      // Core permission check
      canView,
      
      // Role info
      role,
      orgRole,
      platformRole: profile?.platform_role,
      
      // User type booleans
      isAuthenticated,
      isPlatformUser,
      isOrgUser,
      isStudent,
      isPlatformAdmin,
      isDoorStaff,
      
      // Access checks
      canAccessOrgRoutes,
      canAccessPlatformRoutes,
      hasOrgRole,
      
      // Helpers
      getDefaultPath,
      filterNavItems,
      allowedRoutes,
    };
  }, [user, profile, organization, orgRole]);
  
  return permissions;
};

export default usePermissions;
